import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ImageOff } from 'lucide-react';
import DataTable from '../../components/admin/DataTable';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import ErrorState from '../../components/common/ErrorState';
import { fetchWreaths } from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import { formatCurrency } from '../../utils/formatters';

// 이미지 로드 실패 폴백 썸네일
const Thumb = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100 text-gray-400">
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className="h-12 w-12 rounded-md border border-gray-200 object-cover"
    />
  );
};

const WreathManagePage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [wreaths, setWreaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stockTarget, setStockTarget] = useState(null); // 재고 토글 확인 대상

  const loadWreaths = async () => {
    setLoading(true);
    setError(false);
    try {
      const data = await fetchWreaths();
      setWreaths(data);
    } catch (e) {
      console.error('화환 데이터 로드 실패:', e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWreaths();
  }, []);

  // 카테고리 동적 생성 (하드코딩 제거)
  const categories = useMemo(
    () => Array.from(new Set(wreaths.map((w) => w.category).filter(Boolean))),
    [wreaths]
  );

  const filteredWreaths = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return wreaths.filter((w) => {
      const matchCat = filterCategory === 'all' || w.category === filterCategory;
      const matchSearch =
        !term ||
        w.name?.toLowerCase().includes(term) ||
        (Array.isArray(w.features) && w.features.some((f) => f.toLowerCase().includes(term)));
      return matchCat && matchSearch;
    });
  }, [wreaths, filterCategory, searchTerm]);

  const confirmDelete = () => {
    setWreaths((prev) => prev.filter((w) => w.id !== deleteTarget));
    showToast('화환 상품이 삭제되었습니다.', 'success');
    setDeleteTarget(null);
  };

  const confirmStockToggle = () => {
    setWreaths((prev) =>
      prev.map((w) => (w.id === stockTarget.id ? { ...w, inStock: !w.inStock } : w))
    );
    showToast('재고 상태가 변경되었습니다.', 'success');
    setStockTarget(null);
  };

  const columns = [
    { key: 'id', label: 'ID', sortable: true, sortType: 'number' },
    {
      key: 'image', label: '썸네일', sortable: false,
      render: (image, row) => <Thumb src={image} alt={row.name} />,
    },
    { key: 'name', label: '상품명', sortable: true },
    { key: 'category', label: '카테고리', sortable: true },
    { key: 'price', label: '가격', sortable: true, sortType: 'number', render: (p) => formatCurrency(p) },
    {
      key: 'inStock', label: '재고', sortable: true, sortType: 'number',
      render: (inStock, row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setStockTarget(row); }}
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            inStock ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {inStock ? '재고있음' : '품절'}
        </button>
      ),
    },
    {
      key: 'actions', label: '관리', sortable: false,
      render: (_, row) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/admin/wreaths/edit/${row.id}`); }}
            className="text-sm font-medium text-blue-600 hover:text-blue-800"
          >
            수정
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.id); }}
            className="text-sm font-medium text-red-600 hover:text-red-800"
          >
            삭제
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold text-gray-900">화환 상품 관리</h1>
        <Button variant="primary" onClick={() => navigate('/admin/wreaths/new')}>
          <span className="inline-flex items-center gap-1">
            <Plus className="h-4 w-4" /> 새 상품 등록
          </span>
        </Button>
      </div>

      {error ? (
        <ErrorState onRetry={loadWreaths} />
      ) : (
        <>
          {/* 검색 + 카테고리 필터(동적) */}
          <div className="mb-4 space-y-3">
            <input
              type="text"
              placeholder="상품명 또는 꽃 종류로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-primary-500 md:w-96"
            />
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilterCategory('all')}
                className={`rounded-md px-4 py-2 ${
                  filterCategory === 'all' ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                }`}
              >
                전체
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilterCategory(cat)}
                  className={`rounded-md px-4 py-2 ${
                    filterCategory === cat ? 'bg-primary-600 text-white' : 'bg-gray-200 text-gray-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          <DataTable
            columns={columns}
            data={filteredWreaths}
            onRowClick={(w) => navigate(`/wreaths/${w.id}`)}
            emptyMessage="조건에 맞는 화환 상품이 없습니다."
            loading={loading}
            pageSize={10}
          />

          {!loading && (
            <div className="mt-4 text-sm text-gray-600">
              총 {filteredWreaths.length}개 상품이 있습니다.
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="상품 삭제"
        message="이 화환 상품을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다."
        confirmLabel="삭제"
        confirmVariant="danger"
      />
      <ConfirmModal
        isOpen={stockTarget !== null}
        onClose={() => setStockTarget(null)}
        onConfirm={confirmStockToggle}
        title="재고 상태 변경"
        message={
          stockTarget
            ? `"${stockTarget.name}" 상품을 ${stockTarget.inStock ? '품절' : '재고있음'} 상태로 변경하시겠습니까?`
            : ''
        }
        confirmLabel="변경"
      />
    </div>
  );
};

export default WreathManagePage;
